<template>
    <!-- Vente clôturée : plus rien ne peut être ajouté au panier (NF525 — le serveur refuse déjà
         toute écriture). On neutralise tout le catalogue, pas seulement les boutons d'ajout. -->
    <div class="cv3-catalog" :class="{ 'cv3-catalog-locked': locked }" :aria-disabled="locked">
        <!-- search -->
        <div class="cv3-search">
            <SearchIcon :size="20" />
            <input v-model="searchValue" type="text" :placeholder="$t('checkout.v3.search_item_placeholder')" @input="onSearch" />
        </div>

        <!-- breadcrumb -->
        <nav v-if="breadcrumb.length" class="cv3-breadcrumb">
            <button type="button" class="cv3-crumb" @click="goRoot">
                <ChevronLeftIcon :size="14" /> {{ $t('checkout.v3.caisse_crumb') }}
            </button>
            <template v-for="(b, i) in breadcrumb">
                <span :key="'sep-' + i" class="cv3-crumb-sep">›</span>
                <button :key="'bc-' + i" type="button" class="cv3-crumb" @click="goCrumb(i)">{{ b.label }}</button>
            </template>
        </nav>

        <div v-if="loading" class="cv3-loading">{{ $t('checkout.v3.loading') }}</div>

        <!-- TOP-LEVEL CATEGORY GRID -->
        <div v-else-if="view === 'root'" class="cv3-grid">
            <button
                v-for="tile in tiles"
                :key="tile.key"
                type="button"
                class="cv3-tile"
                :class="{ disabled: tile.deferred }"
                @click="enterTile(tile)"
            >
                <span class="cv3-tile-icon"
                    ><component
                        :is="tile.icon"
                        :size="26"
                        :style="{ transform: tile.key === 'Activité' ? 'rotate(-45deg)' : 'rotate(0deg)' }"
                /></span>
                <span class="cv3-tile-label">{{ tile.label }}</span>
                <span v-if="tile.deferred" class="cv3-pos-badge">
                    <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#dbc1f0"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <rect x="2" y="4" width="20" height="12" rx="2"></rect>
                        <path d="M8 20h8M12 16v4"></path>
                    </svg>
                    {{ $t(tile.badgeKey || 'checkout.v3.soon') }}
                </span>
            </button>
        </div>

        <!-- CATEGORY NAVIGATION (nested fallback) -->
        <div v-else-if="view === 'nav'" class="cv3-grid">
            <button v-for="(card, i) in navCards" :key="i" type="button" class="cv3-tile sm" @click="onNavCard(card)">
                <span class="cv3-tile-icon" :style="card.color ? { background: hexa(card.color) } : null">
                    <FolderIcon :size="22" />
                </span>
                <span class="cv3-tile-label">{{ card.label }}</span>
            </button>
            <p v-if="!navCards.length" class="cv3-empty">{{ $t('checkout.v3.no_category') }}</p>
        </div>

        <div v-else-if="view === 'tags'">
            <div class="cv3-chips">
                <button
                    type="button"
                    class="cv3-chip"
                    :class="{ active: selectedCategoryId === null }"
                    @click="selectedCategoryId = null"
                >
                    <SparklesIcon :size="15" /> {{ $t('checkout.v3.all_tags') }}
                </button>
                <button
                    v-for="c in catalogChips"
                    :key="c.id"
                    type="button"
                    class="cv3-chip"
                    :class="{ active: selectedCategoryId === c.id }"
                    @click="selectedCategoryId = c.id"
                >
                    {{ c.label }}
                </button>
            </div>

            <div class="cv3-items">
                <div v-for="item in catalogFilteredItems" :key="'cg-' + item.id" class="cv3-item-card" @click="onItemClick(item)">
                    <div class="cv3-item-img" :style="imgStyle(item)">
                        <span v-if="item._chips && item._chips.length" class="cv3-item-badge">{{ item._chips[0].label }}</span>
                        <span v-if="!itemImage(item)" class="cv3-item-img-ph"><ImageIcon :size="28" /></span>
                    </div>
                    <div class="cv3-item-meta">
                        <span class="cv3-item-name">{{ item.name }}</span>
                        <span class="cv3-item-price">{{ formatPrice(itemPrice(item)) }}€</span>
                    </div>

                    <!-- BOWLING (ligne sèche à paliers) : popover formule + effectif — même chassis
                     que le popover d'options, logique propre (party_sizes serveur). Rendu AVANT
                     celui des options : un bowling porte des prices_by_times, la condition
                     d'en-bas matcherait sinon elle aussi. HORS « Billet non daté » (undatedTicket) :
                     un billet se vend au popover d'options, option × quantité, sans palier
                     (décision 2026-09-22), comme le VR. -->
                    <BookingPopover
                        v-if="openItemId === item.id && item.requires_booking && !undatedTicket"
                        :prestation="item"
                        @confirm="onConfirmBooking"
                    />
                    <!-- duration / option popover -->
                    <OptionsPopover
                        v-else-if="openItemId === item.id && hasOptions(item)"
                        :title="item.name"
                        :options="openOptions"
                        :multi="openMulti"
                        @validate="onValidateOptions(item, $event)"
                    />
                </div>
                <p v-if="!catalogFilteredItems.length" class="cv3-empty">
                    {{ $t(currentCategory === 'Produits' ? 'checkout.v3.no_item' : 'checkout.v3.no_experience') }}
                </p>
            </div>
        </div>

        <!-- BONS CADEAUX (fixed amounts) -->
        <div v-else-if="view === 'voucher'" class="cv3-grid">
            <button v-for="a in voucherAmounts" :key="a" type="button" class="cv3-amount" @click="addVoucher(a)">
                <GiftIcon :size="26" />
                <span class="cv3-amount-val">{{ a }}€</span>
            </button>
            <div class="cv3-amount free">
                <input v-model.number="freeAmount" type="number" min="1" :placeholder="$t('checkout.v3.free_amount')" />
                <button type="button" class="cv3-add-free" :disabled="!freeAmount" @click="addVoucher(freeAmount)">
                    <PlusIcon :size="18" />
                </button>
            </div>
        </div>

        <!-- WALLET top-up (fixed amounts + free) -->
        <div v-else-if="view === 'wallet'" class="cv3-grid">
            <button v-for="a in walletAmounts" :key="a" type="button" class="cv3-amount" @click="addWalletAmount(a)">
                <WalletMinimal :size="26" />
                <span class="cv3-amount-val">{{ a }}€</span>
            </button>
            <div class="cv3-amount free">
                <input v-model.number="freeAmount" type="number" min="1" :placeholder="$t('checkout.v3.free_amount')" />
                <button type="button" class="cv3-add-free" :disabled="!freeAmount" @click="addWalletAmount(freeAmount)">
                    <PlusIcon :size="18" />
                </button>
            </div>
        </div>

        <!-- ITEM GRID (prestations / products / search results) -->
        <div v-else-if="view === 'items'" class="cv3-items">
            <div v-for="item in items" :key="item.id" class="cv3-item-card" @click="onItemClick(item)">
                <div class="cv3-item-img" :style="imgStyle(item)">
                    <span v-if="!itemImage(item)" class="cv3-item-img-ph"><ImageIcon :size="28" /></span>
                </div>
                <div class="cv3-item-meta">
                    <span class="cv3-item-name">{{ item.name }}</span>
                    <span class="cv3-item-price">{{ formatPrice(itemPrice(item)) }}€</span>
                </div>

                <!-- BOWLING (ligne sèche à paliers) : popover formule + effectif — même chassis
                     que le popover d'options, logique propre (party_sizes serveur). Rendu AVANT
                     celui des options : un bowling porte des prices_by_times, la condition
                     d'en-bas matcherait sinon elle aussi. HORS « Billet non daté » (undatedTicket) :
                     un billet se vend au popover d'options, option × quantité, sans palier
                     (décision 2026-09-22), comme le VR. -->
                <BookingPopover
                    v-if="openItemId === item.id && item.requires_booking && !undatedTicket"
                    :prestation="item"
                    @confirm="onConfirmBooking"
                />
                <!-- duration / option popover -->
                <OptionsPopover
                    v-else-if="openItemId === item.id && hasOptions(item)"
                    :title="item.name"
                    :options="openOptions"
                    :multi="openMulti"
                    @validate="onValidateOptions(item, $event)"
                />
            </div>
            <p v-if="!items.length" class="cv3-empty">{{ $t('checkout.v3.no_item') }}</p>
        </div>
    </div>
</template>

<script>
import {
    Search as SearchIcon,
    ChevronLeft as ChevronLeftIcon,
    Folder as FolderIcon,
    Gift as GiftIcon,
    Plus as PlusIcon,
    Image as ImageIcon,
    Sparkles as SparklesIcon,
    Tag as TagIcon,
    Utensils as UtensilsIcon,
    CreditCard as CreditCardIcon,
    Ticket as TicketIcon,
    WalletMinimal,
} from 'lucide-vue'
import BookingPopover from './BookingPopover.vue'
import OptionsPopover from './OptionsPopover.vue'

export default {
    name: 'Catalog',
    components: {
        SearchIcon,
        ChevronLeftIcon,
        FolderIcon,
        GiftIcon,
        PlusIcon,
        ImageIcon,
        CreditCardIcon,
        SparklesIcon,
        OptionsPopover,
        BookingPopover,
        WalletMinimal,
    },
    props: { store: { type: Object, required: true } },
    data() {
        return {
            node: this.store.categories || {},
            breadcrumb: [], // [{ label, node }]
            view: 'root', // root | nav | items | voucher
            items: [],
            loading: false,
            searchValue: '',
            searchTimer: null,
            currentCategory: null,
            isVoucher: false,
            undatedTicket: false,
            // item option popover — les options sont figées à l'ouverture (références stables : le
            // surlignage `selectedOpt === opt` du popover casserait si on les recréait à chaque rendu).
            openItemId: null,
            openOptions: [],
            // Prestation bowling dont le popover est ouvert : référence pour le toast de
            // confirmation (le popover vit DANS la carte, comme celui des options).
            bookingItem: null,
            // bons cadeaux
            voucherAmounts: [25, 50, 100, 150],
            // wallet top-up presets
            walletAmounts: [20, 50, 100],
            freeAmount: null,
            // Chip catalog (Expériences + Produits): flat item list shown as cards, filtered by category chip.
            chipItems: [],
            selectedCategoryId: null,
        }
    },
    mounted() {
        document.addEventListener('click', this.onOutsideClick)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onOutsideClick)
    },
    computed: {
        // Vente clôturée (status_id 7 / signée) : document fiscal figé, aucun ajout possible.
        locked() {
            return !!this.store.isClotured
        },
        // Variantes de billet simple : on peut en prendre PLUSIEURS d'un coup, chacune avec sa
        // quantité (2 adultes + 3 enfants). Les durées restent un choix unique.
        openMulti() {
            return !!(this.openOptions[0] && this.openOptions[0]._ticket)
        },
        tiles() {
            const cats = this.store.categories || {}
            const def = [
                { key: 'Activité', label: this.$t('checkout.categories.activity'), icon: TicketIcon, deferred: false },
                { key: 'Produits', label: this.$t('checkout.categories.products'), icon: TagIcon, deferred: false },
                { key: 'Bons cadeau', label: this.$t('checkout.categories.vouchers'), icon: GiftIcon, deferred: false },
                { key: 'Wallet', label: this.$t('checkout.categories.wallet'), icon: WalletMinimal, deferred: false },
                { key: 'Forfaits', label: this.$t('checkout.categories.bundles'), icon: CreditCardIcon, deferred: false },
                {
                    key: 'Billet non daté',
                    label: this.$t('checkout.categories.undated_ticket'),
                    icon: TicketIcon,
                    deferred: false,
                },
                {
                    key: 'Restauration',
                    label: this.$t('food.title'),
                    icon: UtensilsIcon,
                    deferred: true,
                    badgeKey: 'checkout.v3.pos_only',
                    toastKey: 'checkout.v3.pos_only_toast',
                },
            ]
            // Keep tiles whose data exists (or are explicit deferred placeholders). Produits is always shown:
            // even with no product category there may be uncategorized products to sell.
            return def.filter((t) => t.deferred || t.key === 'Produits' || cats[t.key] !== undefined)
        },
        navCards() {
            const n = this.node
            let cards = []
            if (Array.isArray(n)) {
                // array of subcategory objects (+ trailing color string)
                cards = n.filter((x) => x && typeof x === 'object').map((x) => ({ label: x.name, id: x.id, node: x, leaf: true }))
            } else if (n) {
                // object: map of category name -> children
                cards = Object.keys(n).map((k) => ({ label: k, node: n[k], leaf: false, color: this.colorOf(n[k]) }))
            }
            return cards
        },
        catalogChips() {
            const seen = new Map()
            this.chipItems.forEach((it) => {
                ;(it._chips || []).forEach((c) => {
                    if (!seen.has(c.id)) seen.set(c.id, c)
                })
            })
            return Array.from(seen.values())
        },
        catalogFilteredItems() {
            if (this.selectedCategoryId === null) return this.chipItems
            return this.chipItems.filter((it) => (it._chips || []).some((c) => c.id === this.selectedCategoryId))
        },
    },
    methods: {
        // ---------- helpers ----------
        formatPrice(v) {
            return (Math.round((Number(v) || 0) * 100) / 100).toString().replace('.', ',')
        },
        colorOf(node) {
            if (Array.isArray(node)) {
                const last = node[node.length - 1]
                if (typeof last === 'string' && last) return last
            }
            return null
        },
        hexa(c) {
            return c
        },
        itemImage(item) {
            return (
                item.image_url ||
                item.image ||
                item.picture ||
                item.cover ||
                (item.medias && item.medias[0] && item.medias[0].url) ||
                null
            )
        },
        imgStyle(item) {
            const img = this.itemImage(item)
            return img ? { backgroundImage: `url(${img})` } : null
        },
        // Billet simple : le parent est à 0 € et n'est jamais vendu tel quel — la carte affiche donc
        // le tarif le plus bas parmi ses variantes ("à partir de").
        ticketVariants(item) {
            return Array.isArray(item.ticket_variants) ? item.ticket_variants : []
        },
        variantPrice(v) {
            const pbt = (v.prices_by_times || [])[0]
            return pbt ? pbt.price : v.price
        },
        itemPrice(item) {
            const variants = this.ticketVariants(item)
            if (variants.length) return Math.min(...variants.map((v) => Number(this.variantPrice(v)) || 0))
            if (item.prices_by_times && item.prices_by_times.length) return item.prices_by_times[0].price
            return item.price
        },
        // Options du popover : les variantes du billet simple si le parent en a, sinon les durées.
        // Chaque option de variante embarque la prestation réellement vendue (_ticket) et sa ligne
        // prices_by_times (_pbt), pour que l'ajout au panier cible l'ENFANT et pas le parent à 0 €.
        cardOptions(item) {
            const variants = this.ticketVariants(item)
            if (variants.length) {
                return variants.map((v) => {
                    const pbt = (v.prices_by_times || [])[0] || null
                    return {
                        _ticket: v,
                        _pbt: pbt,
                        price: pbt ? pbt.price : v.price,
                        price_ht: pbt ? pbt.price_ht : v.price_ht,
                    }
                })
            }
            return item.prices_by_times || []
        },
        hasOptions(item) {
            if (this.ticketVariants(item).length) return true
            return Array.isArray(item.prices_by_times) && item.prices_by_times.length > 0
        },
        // ---------- navigation ----------
        goRoot() {
            this.node = this.store.categories
            this.breadcrumb = []
            this.view = 'root'
            this.resetFlags()
        },
        goCrumb(i) {
            const target = this.breadcrumb[i]
            // Back to the Expériences root: the chip-filtered prestation grid.
            if (target.node === '__tags__') {
                this.breadcrumb = [target]
                this.openItemId = null
                this.openOptions = []
                this.view = 'tags'
                return
            }
            this.breadcrumb = this.breadcrumb.slice(0, i)
            this.node = target.node
            this.view = this.breadcrumb.length || target.node ? 'nav' : 'root'
            if (!this.breadcrumb.length) this.goRoot()
        },
        resetFlags() {
            this.isVoucher = false
            this.undatedTicket = false
            this.currentCategory = null
            this.openItemId = null
            this.openOptions = []
            this.items = []
            this.chipItems = []
            this.selectedCategoryId = null
        },
        enterTile(tile) {
            if (tile.deferred) {
                this.$toast && this.$toast.info(this.$t(tile.toastKey || 'checkout.v3.tile_soon_toast', { label: tile.label }))
                return
            }
            this.resetFlags() // clear isVoucher/undatedTicket from a previous module
            this.currentCategory = tile.key
            this.breadcrumb = [{ label: tile.label, node: null }]

            if (tile.key === 'Bons cadeau') {
                this.isVoucher = true
                this.view = 'voucher'
                return
            }
            if (tile.key === 'Wallet') {
                // Wallet top-up: pick / type an amount (credited + bonus server-side).
                this.view = 'wallet'
                return
            }
            if (tile.key === 'Billet non daté') {
                // Undated tickets: same TAG navigation as Expériences (online-or-not, "show all if no tag"),
                // with the establishment's undated price increase applied server-side. A click adds the
                // prestation as an undated-ticket voucher draft (this.undatedTicket routes onItemClick).
                this.breadcrumb = [{ label: tile.label, node: '__tags__' }]
                this.loadCatalogTags({ undated: true })
                return
            }
            if (tile.key === 'Forfaits') {
                // Forfaits = flat array of Treatment models (sortedCategories). Show them as item cards;
                // a click adds the forfait as a cart line (legacy addPresta type=Forfaits → OrderItem Treatment).
                this.items = this.store.categories['Forfaits'] || []
                this.view = 'items'
                return
            }
            if (tile.key === 'Activité') {
                // Experiences are browsed by TAG (like the online front), shown online-or-not.
                this.breadcrumb = [{ label: tile.label, node: '__tags__' }]
                this.loadCatalogTags()
                return
            }
            if (tile.key === 'Produits') {
                // All products as cards, filtered by category chips (no folders).
                this.breadcrumb = [{ label: tile.label, node: '__tags__' }]
                this.loadAllProducts()
                return
            }
            // Fallback: descend into the nested category map.
            this.node = this.store.categories[tile.key]
            this.view = 'nav'
        },
        onNavCard(card) {
            if (card.leaf) {
                // leaf subcategory -> load items
                this.breadcrumb.push({ label: card.label, node: this.node })
                this.loadItems(card.id)
            } else {
                // descend one level
                this.breadcrumb.push({ label: card.label, node: this.node })
                this.node = card.node
                this.view = 'nav'
            }
        },
        async loadItems(subcategoryId) {
            this.loading = true
            this.view = 'items'
            try {
                if (this.currentCategory === 'Produits') {
                    this.items = await this.store.browseProducts(subcategoryId)
                } else {
                    this.items = await this.store.browsePresta(subcategoryId, {
                        isVoucher: this.isVoucher,
                        undatedTicket: this.undatedTicket,
                    })
                }
            } catch (e) {
                this.items = []
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_loading_items'))
            } finally {
                this.loading = false
            }
        },
        // Produits: chips = product subcategory.
        async loadAllProducts() {
            this.loading = true
            this.view = 'tags'
            this.selectedCategoryId = null
            try {
                const products = await this.store.browseAllProducts()
                products.forEach((p) => {
                    const s = p.subcategory
                    p._chips = s && s.id != null ? [{ id: s.id, label: s.name }] : []
                })
                this.chipItems = products
            } catch (e) {
                this.chipItems = []
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_loading_products'))
            } finally {
                this.view = 'tags'
                this.loading = false
            }
        },
        // Expériences: chips = TAGS (les "catégories" du front online), PAS les sous-catégories.
        // L'endpoint catalogByTag renvoie les prestations validées déjà groupées par tag ; on les
        // aplatit en une liste unique par id, chaque carte portant ses tags comme chips de filtre.
        async loadCatalogTags({ undated = false } = {}) {
            this.loading = true
            this.view = 'tags'
            this.selectedCategoryId = null
            this.undatedTicket = undated
            try {
                const d = await this.store.loadCatalogByTag({ undatedTicket: undated })
                const map = new Map()
                ;(d.tags || []).forEach((t) => {
                    ;(t.prestations || []).forEach((p) => {
                        let e = map.get(p.id)
                        if (!e) {
                            e = p
                            e._chips = []
                            map.set(p.id, e)
                        }
                        if (!e._chips.some((c) => c.id === t.id)) e._chips.push({ id: t.id, label: t.name })
                    })
                })
                ;(d.untagged || []).forEach((p) => {
                    if (!map.has(p.id)) {
                        p._chips = []
                        map.set(p.id, p)
                    }
                })
                this.chipItems = Array.from(map.values())
            } catch (e) {
                this.chipItems = []
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_loading_catalog'))
            } finally {
                this.view = 'tags'
                this.loading = false
            }
        },
        // ---------- search ----------
        onSearch() {
            clearTimeout(this.searchTimer)
            const q = this.searchValue
            if (!q) {
                this.goRoot()
                return
            }
            this.searchTimer = setTimeout(async () => {
                this.loading = true
                this.view = 'items'
                try {
                    this.items = await this.store.research(q, this.currentCategory)
                } catch (e) {
                    this.items = []
                } finally {
                    this.loading = false
                }
            }, 450)
        },
        // ---------- add ----------
        onItemClick(item) {
            if (this.currentCategory === 'Produits') {
                this.addProduct(item)
                return
            }
            // Drapeau posé par le SERVEUR (`catalogByTag` / `research`) : le Vue n'a pas à
            // connaître la liste des circuits. Testé AVANT les options — un bowling porte des
            // durées, mais il ne se vend JAMAIS par le circuit générique. EXCEPTION « Billet
            // non daté » : le popover à paliers n'a pas lieu d'être — un billet est un objet
            // par personne au prix de l'option (majorée du surplus billet, donnée serveur),
            // les paliers se résolvent au rachat. Le bowling suit alors EXACTEMENT le VR :
            // popover d'options -> brouillon bon -> bouton « Valider le billet non daté ».
            if (item.requires_booking && !this.undatedTicket) {
                // Popover formule + effectif, même geste au comptoir que le popover d'options :
                // toggle au clic, fermeture au clic extérieur. La logique (effectifs vendables,
                // formules) vit dans BookingPopover ; `bookingItem` garde la référence pour le toast.
                this.openItemId = this.openItemId === item.id ? null : item.id
                this.bookingItem = this.openItemId ? item : null
                return
            }
            if (this.hasOptions(item)) {
                this.openItemId = this.openItemId === item.id ? null : item.id
                this.openOptions = this.openItemId ? this.cardOptions(item) : []
                return
            }
            if (this.undatedTicket) {
                this.addUndatedTicket(item, null, 1)
                return
            }
            this.addPresta(item, null, 1)
        },
        async addProduct(item) {
            try {
                await this.store.addProduct(item.id)
                this.flash(item.name)
            } catch (e) {
                this.addError()
            }
        },
        // Variante de billet simple : c'est l'ENFANT qui part au panier (id, libellé, prix et ligne
        // prices_by_times), le parent n'est qu'un regroupement d'affichage à 0 €.
        soldItem(item, opt) {
            return opt && opt._ticket ? opt._ticket : item
        },
        soldTimeOption(opt) {
            if (!opt) return null
            return opt._ticket ? opt._pbt : opt
        },
        async addPresta(item, opt, qty, { silent = false } = {}) {
            const sold = this.soldItem(item, opt)
            try {
                await this.store.addPresta({
                    type: this.currentCategory === 'Forfaits' ? 'Forfaits' : 'Activité',
                    item_id: sold.id,
                    label: sold.name,
                    price: opt ? opt.price : sold.price,
                    price_ht: opt ? opt.price_ht : sold.price_ht,
                    quantity: qty || 1,
                    timeOption: this.soldTimeOption(opt),
                })
                if (!silent) this.flash(sold.name)
                return true
            } catch (e) {
                // Un refus délibéré du serveur (422) porte un message traduit : le relayer au
                // lieu du toast générique. cfg.validateStatus rejette >= 400, donc e.response.data
                // est le JSON { message } de Laravel.
                if (!silent) this.addError(e?.response?.data?.message)
                return false
            }
        },
        /**
         * Validation du popover — le composant émet TOUJOURS une liste [{ opt, qty }] : un seul
         * élément pour une durée, autant que de variantes choisies pour un billet simple.
         */
        async onValidateOptions(item, picks) {
            const list = (picks || []).filter((p) => p && p.opt && p.qty > 0)
            if (!list.length) return
            this.closePopover()

            // Billet simple (hors "Billet non daté") : on écrit la structure V2 en UN appel — parent
            // (prix 0, qté = somme) + une ligne enfant par variante. Le serveur décide des prix.
            const isSimpleTicket = list.every((p) => p.opt && p.opt._ticket)
            if (isSimpleTicket && !this.undatedTicket) {
                try {
                    await this.store.addSimpleTicket({
                        parentId: item.id,
                        options: list.map((p) => ({ id: p.opt._ticket.id, quantity: p.qty })),
                    })
                    const label = list
                        .map((p) => (p.qty > 1 ? `${p.qty} × ${p.opt._ticket.name}` : p.opt._ticket.name))
                        .join(', ')
                    this.flash(label)
                } catch (e) {
                    this.addError()
                }
                return
            }

            // Durée unique, ou variantes en "Billet non daté" (chaque variante = un billet non daté).
            // Ajouts SÉQUENTIELS : le premier crée la commande, les suivants s'y rattachent (un envoi
            // parallèle partirait sans order_id et ouvrirait autant de ventes séparées).
            const added = []
            let failed = 0
            for (const pick of list) {
                const sold = this.soldItem(item, pick.opt)
                const ok = this.undatedTicket
                    ? await this.addUndatedTicket(item, pick.opt, pick.qty, { silent: true })
                    : await this.addPresta(item, pick.opt, pick.qty, { silent: true })
                if (ok) {
                    added.push(pick.qty > 1 ? `${pick.qty} × ${sold.name}` : sold.name)
                } else {
                    failed++
                }
            }
            if (added.length) this.flash(added.join(', '))
            if (failed) this.addError()
        },
        closePopover() {
            this.openItemId = null
            this.openOptions = []
            this.bookingItem = null
        },
        onOutsideClick(e) {
            // close the item popovers (options / bowling) when clicking anywhere outside an item card
            if (this.openItemId === null) return
            if (this.$el && !e.target.closest('.cv3-item-card')) this.closePopover()
        },
        async addVoucher(amount) {
            if (!amount) return
            try {
                await this.store.addVoucher({
                    amount,
                    label: this.$t('checkout.v3.item_added', { name: `${this.$t('checkout.v3.voucher_tag')} ${amount}€` }),
                })
                this.flash(`${this.$t('checkout.v3.voucher_tag')} ${amount}€`)
                this.freeAmount = null
            } catch (e) {
                this.addError()
            }
        },
        async addWalletAmount(amount) {
            if (!amount) return
            const c = this.store.currentCustomer
            if (!this.store.userId || !c || c === false || c.is_guest) {
                this.$toast && this.$toast.info(this.$t('checkout.v3.select_client_wallet'))
                return
            }
            try {
                await this.store.addWallet({ amount })
                this.flash(`Wallet +${amount}€`)
                this.freeAmount = null
            } catch (e) {
                this.addError()
            }
        },
        // « Billet non daté » : un brouillon de bon PAR BILLET, au prix de l'option choisie. Pour
        // un bowling, addVoucher relit l'option côté serveur : le montant posté ne fait pas foi.
        async addUndatedTicket(item, opt, qty, { silent = false } = {}) {
            const sold = this.soldItem(item, opt)
            const price = opt ? opt.price : sold.price
            try {
                await this.store.addUndatedTicket({
                    itemId: sold.id,
                    amount: price,
                    quantity: qty || 1,
                    price,
                    price_ht: opt ? opt.price_ht : sold.price_ht,
                    timeOption: this.soldTimeOption(opt),
                })
                if (!silent) this.flash(`${this.$t('checkout.v3.voucher_tag')} · ${sold.name}`)
                return true
            } catch (e) {
                if (!silent) this.addError()
                return false
            }
        },
        // Bowling confirmé dans le popover : LIGNE SÈCHE, comme les autres circuits (décision
        // produit 2026-09-18 — plus aucune piste bloquée, plus aucun créneau). Le serveur a
        // résolu prix et durée depuis l'effectif ; le panier rafraîchi par `mutateCart` EST la
        // seule source de vérité sur ce qui vient d'être vendu.
        async onConfirmBooking(payload) {
            const item = this.bookingItem
            try {
                const booked = await this.store.addBooking(payload)
                // `null` = la mutation n'a PAS eu lieu (double-clic). Ne rien annoncer, ne rien
                // fermer : afficher un succès pour une ligne inexistante était pire que rien.
                if (booked === null) return

                this.closePopover()
                // `payload` ne porte pas de nom : c'est la CARTE qui l'a.
                this.flash((item && item.name) || '')
            } catch (e) {
                // Le refus serveur est déjà rédigé et traduit (client non sélectionné, effectif
                // hors paliers, formule inconnue, panier clôturé NF525) : l'afficher tel quel.
                // Le store ne connaît pas la langue, donc c'est ici que vivent les replis traduits.
                let message = (e && e.message) || ''
                if (message === 'CLOSED_SALE') message = this.$t('checkout.v3.closed_readonly')
                this.$toast && this.$toast.error(message || this.$t('checkout.v3.error_adding_item'))
            }
        },
        flash(name) {
            this.$toast && this.$toast.success(this.$t('checkout.v3.item_added', { name }))
        },
        // Message du serveur (refus 422 traduit) s'il existe, sinon la clé générique.
        addError(msg) {
            this.$toast && this.$toast.error(msg || this.$t('checkout.v3.error_adding_item'))
        },
    },
}
</script>

<style scoped lang="scss">
.cv3-catalog-locked {
    pointer-events: none;
    opacity: 0.4;
    filter: grayscale(0.6);
    user-select: none;
}
.cv3-catalog {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: 4px;
}
.cv3-search {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 15px 18px;
    border-radius: 13px;
    border: 1px solid var(--cv3-border);
    background: var(--cv3-search);
    box-shadow: var(--cv3-shadow);
    color: var(--cv3-muted);
    input {
        flex: 1 1 auto;
        background: transparent;
        border: 0;
        outline: 0;
        color: var(--cv3-text);
        font-size: 15px;
        &::placeholder {
            color: rgba(255, 255, 255, 0.6);
        }
    }
}
.cv3-breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.cv3-crumb {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border-radius: 9px;
    border: 1px solid var(--cv3-border);
    background: rgba(255, 255, 255, 0.03);
    color: var(--cv3-text-2);
    font-size: 13px;
    font-weight: 600;
}
.cv3-crumb-sep {
    color: var(--cv3-muted);
}
.cv3-loading,
.cv3-empty {
    color: var(--cv3-muted);
    padding: 24px 4px;
    font-size: 14px;
}

/* category tiles */
.cv3-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
}
.cv3-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    height: 130px;
    border-radius: 14px;
    border: 1px solid var(--cv3-border);
    background: var(--cv3-tile);
    box-shadow: var(--cv3-shadow);
    color: var(--cv3-text);
    &.sm {
        height: 100px;
    }
    &.disabled {
        .cv3-tile-icon,
        .cv3-tile-label {
            opacity: 0.55;
        }
    }
    &:hover:not(.disabled) {
        border-color: var(--cv3-accent);
    }
}
.cv3-tile-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: var(--cv3-icon-tile);
    color: #fff;
}
.cv3-tile-label {
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    line-height: 1.3;
    padding: 0 6px;
}
.cv3-pos-badge {
    position: absolute;
    top: -9px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
    background: rgb(59, 18, 100);
    border: 1px solid rgba(171, 110, 220, 0.6);
    border-radius: 999px;
    padding: 3px 9px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: rgb(233, 216, 250);
    z-index: 2;
}
.cv3-tag-count {
    position: absolute;
    top: 10px;
    right: 10px;
    min-width: 20px;
    text-align: center;
    font-size: 11px;
    font-weight: 800;
    color: var(--cv3-text);
    background: var(--cv3-icon-tint);
    border-radius: 999px;
    padding: 1px 7px;
}

/* bons cadeaux amounts */
.cv3-amount {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    height: 130px;
    border-radius: 17px;
    border: 1px solid var(--cv3-border);
    background: var(--cv3-tile);
    color: var(--cv3-text);
    &.free {
        flex-direction: row;
        gap: 8px;
        padding: 0 14px;
        border-style: dashed;
    }
    &:hover {
        border-color: var(--cv3-accent);
    }
    input {
        width: 100%;
        background: transparent;
        border: 0;
        outline: 0;
        color: var(--cv3-text);
        font-size: 16px;
        text-align: center;
        &::placeholder {
            color: var(--cv3-muted);
        }
    }
}
.cv3-amount-val {
    font-size: 20px;
    font-weight: 800;
    font-family: 'Plus Jakarta Sans', sans-serif;
}
.cv3-add-free {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    border: 0;
    background: var(--cv3-accent);
    color: #fff;
    flex-shrink: 0;
}

/* tag filter chips (Expériences) */
.cv3-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 16px;
}
.cv3-chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 16px;
    border-radius: 999px;
    border: 1px solid var(--cv3-border-input);
    background: rgba(255, 255, 255, 0.03);
    color: var(--cv3-text-2);
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    &:hover {
        border-color: var(--cv3-accent);
    }
    &.active {
        background: var(--cv3-accent);
        border-color: var(--cv3-accent);
        color: #fff;
    }
}

/* item (prestation/product) cards */
.cv3-items {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 12px;
}
.cv3-item-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;
    max-width: calc(100% - 16px);
    padding: 3px 9px;
    border-radius: 999px;
    background: rgba(6, 11, 38, 0.78);
    border: 1px solid var(--cv3-border-input);
    color: var(--cv3-text);
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.cv3-item-card {
    position: relative;
    border-radius: 14px;
    overflow: visible;
    cursor: pointer;
    background: var(--cv3-tile);
    border: 1px solid var(--cv3-border);
    transition: border-color 0.15s;
    &:hover {
        border-color: var(--cv3-accent);
    }
}
.cv3-item-img {
    height: 100px;
    border-radius: 14px 14px 0 0;
    background-size: cover;
    background-position: center;
    background-color: #2a2752;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}
.cv3-item-img-ph {
    color: var(--cv3-muted);
}
.cv3-item-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 8px 10px;
    gap: 3px;
}
.cv3-item-name {
    font-weight: 700;
    font-size: 12px;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.cv3-item-price {
    font-weight: 800;
    font-size: 13px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    color: var(--cv3-accent);
}
/* Le popover d'options (durées / variantes de billet simple) vit dans OptionsPopover.vue —
   styles inclus, pour que les deux grilles de cartes partagent exactement le même. */
</style>
